import { Alert, Button, Spin } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withUserType } from '../withUserType';
import DateTimeHover from '../date_time_component/DateTime';
import useMountEffect from '../../hooks/useMountEffect';
import OscarMark from '../../ai_agent/components/shared/OscarMark';
import { AOPEvent, pageRows } from '../../types/AOPExecutionTypes';
import { readExecutionEvents } from '../../thunks/agent_executions_thunks';
import { describeEvent, isListedEvent } from '../aop_builder/utils/AOPExecutionUtils';
import AOPEventSentence from '../aop_builder/AOPEventSentence';

/** Figma marks a finished step with a small tick; Oscar stands in for a live one. */
const MARK_SIZE = 18;

/**
 * `limit` is capped at 100 upstream, and a run can outrun that: one decision
 * with escalations wrote fourteen events in two minutes, and a plan may hold a
 * hundred steps.
 *
 * So one page is read and the rest is asked for. Walking the cursor to the end
 * on open pre-fetched up to two thousand events nobody had scrolled to, and
 * stopped there whether or not that was the end — presenting a cut-off
 * transcript as a whole one, with the last event it happened to hold animated
 * as Radar's current step.
 */
const PAGE_SIZE = 100;

/**
 * One run of a procedure against one work order, oldest event first — the order
 * Radar did it in, so it reads top to bottom like a transcript.
 *
 * The events are read when the session is opened rather than with the list: a
 * work order can have several sessions and only one of them is usually being
 * looked at.
 */
const RadarSessionTimeline: FC<any> = ({ execution, userType, timeZoneId, loadEvents }) => {
	const [events, setEvents] = useState<AOPEvent[]>([]);
	// Where the next page starts, or null once the transcript is whole.
	const [cursor, setCursor] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	/*
	  Two errors, not one. Failing the first page leaves nothing to show, so the
	  message is all there is. Failing a later one leaves a transcript already on
	  screen, and replacing it with the message takes away both what had been
	  read and the button that would have tried again.
	*/
	const [error, setError] = useState<string | null>(null);
	const [pageError, setPageError] = useState<string | null>(null);

	const load = useCallback(
		(after: string | null) =>
			loadEvents(execution.id, after, PAGE_SIZE).then((page) => {
				// Filtered as it arrives rather than at render, so the empty state and
				// the "this is the step in progress" mark both read off what is shown.
				const listed = pageRows<AOPEvent>(page).filter(isListedEvent);

				setEvents((previous) => (after === null ? listed : previous.concat(listed)));
				setCursor(page.next_cursor ? page.next_cursor : null);
				setPageError(null);
			}),
		[execution.id, loadEvents]
	);

	useMountEffect(() => {
		load(null)
			.catch(() => setError('Could not load what happened in this session.'))
			.then(() => setIsLoading(false));
	});

	const onLoadMore = useCallback(() => {
		setIsLoadingMore(true);
		load(cursor)
			.catch(() => setPageError('Could not load the rest of this session.'))
			.then(() => setIsLoadingMore(false));
	}, [cursor, load]);

	if (isLoading) {
		return (
			<div className="flex justify-center py-l">
				<Spin size="small" />
			</div>
		);
	}

	if (error) {
		return <div className="bodyregular px-m py-l text-character-secondary">{error}</div>;
	}

	// Only when the feed is exhausted. A page can hold nothing worth listing —
	// every line on it filtered out — while the cursor points at events that are,
	// and returning here would take away the only way to reach them.
	if (events.length === 0 && cursor === null) {
		return (
			<div className="bodyregular px-m py-l text-character-secondary">
				Radar has not recorded anything in this session yet.
			</div>
		);
	}

	const lastIndex = events.length - 1;
	// Only the end of a whole transcript is the step in progress. With a page
	// still to come, the last event held is simply the last one read.
	const hasMore = cursor !== null;
	// `paused` is its own state now, so this is just "is it working" — a held run
	// stops the last mark flapping without needing to be asked about separately.
	const isRunning = execution.state === 'running';

	return (
		<div className="flex flex-col bg-grey-1">
			{/*
			  A page can filter down to nothing while the cursor points at events
			  that would show. Column headings over no rows would read as an empty
			  session, which is the thing this is here to avoid saying.
			*/}
			{events.length === 0 ? (
				<div className="bodyregular px-m pt-m text-character-secondary">
					Nothing on this page was worth listing. Load more to read on.
				</div>
			) : (
				<div className="flex items-center gap-m border-0 border-b border-solid border-grey-4 px-m py-s">
					<span className="footnoteMedium w-[48px] shrink-0" />
					<span className="footnoteMedium w-[140px] shrink-0 text-character-secondary">Time</span>
					<span className="footnoteMedium flex-1 text-character-secondary">Steps</span>
				</div>
			)}

			{events.map((event, index) => {
				const described = describeEvent(event, timeZoneId);
				// The last step of a run that is still going is the one in progress;
				// everything behind it has happened. A finished run has no live step.
				const isLive = isRunning && !hasMore && index === lastIndex;

				return (
					/*
					  No vertical padding on the row: the thread between the marks is a
					  flex child of the marker column, and it can only reach the next
					  mark if the column runs the full height of the row. The padding
					  lives on the two text columns instead.
					*/
					<div key={event.sequence} className="flex items-stretch gap-m px-m">
						<div className="flex w-[48px] shrink-0 flex-col items-center">
							{/*
							  Two pieces of thread per row, one above the mark and one below,
							  because a piece can only reach its own row's edge. The piece
							  above is as tall as the text columns' top padding, so the mark
							  lines up with the first line of text — and it starts where the
							  piece below the previous mark stopped, which is what joins
							  them. The first row has nothing above it to join to.
							*/}
							<span className={`h-s w-px ${index === 0 ? '' : 'bg-lifecycleChange-negative'}`} />
							<span
								className={`leading-none ${
									isLive ? 'text-purple-6' : 'text-lifecycleChange-negative'
								}`}
							>
								{isLive ? (
									<OscarMark size={MARK_SIZE} animation="flap" />
								) : (
									<CheckCircleFilled style={{ fontSize: MARK_SIZE }} />
								)}
							</span>
							{/* Takes whatever height the row has left; the last has nothing
							    left to reach. */}
							{index === lastIndex ? null : (
								<span className="w-px flex-1 bg-lifecycleChange-negative" />
							)}
						</div>

						<div className="flex w-[140px] shrink-0 flex-col py-s">
							<span className="bodyregular text-character-primary">
								{/*
								  `showDate` keeps it an absolute time: these read as a
								  sequence, and "3 minutes ago" on every row of one would say
								  nothing about the order they happened in.
								*/}
								<DateTimeHover
									timestamp={event.occurred_at}
									timeZone={timeZoneId}
									showDate
									dateFormat="h:mm:ss a z"
								/>
							</span>
							<span className="footnoteregular text-character-secondary">
								<DateTimeHover
									timestamp={event.occurred_at}
									timeZone={timeZoneId}
									showDate
									dateFormat="D MMM, YYYY"
								/>
							</span>
						</div>

						<div className="flex min-w-0 flex-1 flex-col gap-2xs py-s">
							<span className="bodyregular text-character-primary">
								<AOPEventSentence described={described} />
							</span>
							{described.supplier || described.detail ? (
								<span className="bodyregular flex flex-wrap items-center gap-2xs text-character-secondary">
									{described.supplier ? (
										<span>
											<Link
												to={`/${userType}/suppliers/detail/${described.supplier.id}`}
												target="_blank"
											>
												{described.supplier.name}
											</Link>{' '}
											assigned.
										</span>
									) : null}
									{described.detail ? <span>{described.detail}</span> : null}
								</span>
							) : null}
						</div>
					</div>
				);
			})}

			{/*
			  The rest of the transcript, asked for rather than pre-fetched. It sits
			  at the end because the events read oldest first, so what is missing is
			  what came after.
			*/}
			{cursor === null ? null : (
				<div className="flex flex-col gap-s px-m py-s">
					{/* Beside the rows, not instead of them: what has been read stays
					    on screen and the button is still there to try again. */}
					{pageError ? <Alert type="error" showIcon message={pageError} /> : null}
					<Button className="self-start" size="small" loading={isLoadingMore} onClick={onLoadMore}>
						{pageError ? 'Try again' : 'Load more'}
					</Button>
				</div>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	// The cursor is an opaque string now: one endpoint serves every event feed,
	// so a run's own history pages the same way the procedure's does.
	// `asc` explicitly: a transcript reads top to bottom in the order Radar did
	// it, and the endpoint's default is not something to depend on — it is `asc`
	// now and was `desc` on the route this replaced.
	loadEvents: (executionId: string, after: string | null, limit: number) =>
		dispatch(readExecutionEvents(executionId, { after, limit, order: 'asc' })),
});

export default withUserType(connect(null, mapDispatchToProps)(RadarSessionTimeline));
